import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';

export interface PendingMeetingToComplete {
  id: string;
  title: string | null;
  scheduledFor: string;
  contactId: string | null;
  contactName: string | null;
  leadId: string | null;
  leadName: string | null;
  hostId: string | null;
  hostName: string | null;
  hoursOverdue: number;
  meetingUrl: string | null;
}

export function usePendingMeetingsToComplete() {
  const [pendingMeetings, setPendingMeetings] = useState<PendingMeetingToComplete[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: appUser } = useCurrentAppUser();

  const loadPendingMeetings = useCallback(async () => {
    if (!appUser?.id) {
      setPendingMeetings([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Find meetings that are overdue by more than 30 minutes
      // scheduled_by = current user (SDR who scheduled)
      // channel = 'video'
      // status = 'scheduled'
      // scheduled_for < NOW() - 30 minutes
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      
      console.log('[PendingMeetings] Checking for overdue meetings:', {
        appUserId: appUser.id,
        threshold: thirtyMinutesAgo
      });

      const { data: meetings, error } = await supabase
        .from('calls')
        .select(`
          id,
          title,
          scheduled_for,
          contact_id,
          lead_id,
          created_by,
          meeting_url
        `)
        .eq('channel', 'video')
        .eq('status', 'scheduled')
        .eq('scheduled_by', appUser.id)
        .lt('scheduled_for', thirtyMinutesAgo)
        .order('scheduled_for', { ascending: true });

      if (error) {
        console.error('[PendingMeetings] Error loading pending meetings:', error);
        setPendingMeetings([]);
        return;
      }

      console.log('[PendingMeetings] Found meetings:', meetings?.length || 0, meetings);

      if (!meetings || meetings.length === 0) {
        setPendingMeetings([]);
        return;
      }

      // Get contact names
      const contactIds = meetings.filter(m => m.contact_id).map(m => m.contact_id);
      const leadIds = meetings.filter(m => m.lead_id).map(m => m.lead_id);
      const hostIds = meetings.filter(m => m.created_by).map(m => m.created_by);

      const [contactsResult, hostsResult] = await Promise.all([
        contactIds.length > 0 
          ? supabase.from('contacts').select('id, first_name, last_name').in('id', contactIds)
          : { data: [] },
        // DEPRECATED - leads query removed
        hostIds.length > 0
          ? supabase.from('app_users').select('id, display_name').in('id', hostIds)
          : { data: [] }
      ]);

      const contactMap = new Map((contactsResult.data || []).map(c => [
        c.id, 
        [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Sem nome'
      ]));
      const leadMap = new Map(); // DEPRECATED - empty map for backward compat
      const hostMap = new Map((hostsResult.data || []).map(h => [h.id, h.display_name]));

      const now = new Date();
      const transformedMeetings: PendingMeetingToComplete[] = meetings.map(meeting => {
        const scheduledDate = new Date(meeting.scheduled_for);
        const hoursOverdue = Math.floor((now.getTime() - scheduledDate.getTime()) / (1000 * 60 * 60));

        return {
          id: meeting.id,
          title: meeting.title,
          scheduledFor: meeting.scheduled_for,
          contactId: meeting.contact_id,
          contactName: meeting.contact_id ? contactMap.get(meeting.contact_id) || null : null,
          leadId: meeting.lead_id,
          leadName: meeting.lead_id ? leadMap.get(meeting.lead_id) || null : null,
          hostId: meeting.created_by,
          hostName: meeting.created_by ? hostMap.get(meeting.created_by) || null : null,
          hoursOverdue,
          meetingUrl: meeting.meeting_url,
        };
      });

      setPendingMeetings(transformedMeetings);
    } catch (err) {
      console.error('Error in usePendingMeetingsToComplete:', err);
      setPendingMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [appUser?.id]);

  const completeMeeting = async (meetingId: string): Promise<boolean> => {
    if (!appUser?.id) return false;

    try {
      const { error } = await supabase
        .from('calls')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: appUser.id,
        })
        .eq('id', meetingId);

      if (error) {
        console.error('Error completing meeting:', error);
        return false;
      }

      // Remove from local state
      setPendingMeetings(prev => prev.filter(m => m.id !== meetingId));
      return true;
    } catch (err) {
      console.error('Error completing meeting:', err);
      return false;
    }
  };

  const cancelMeeting = async (meetingId: string, reason?: string): Promise<boolean> => {
    if (!appUser?.id) return false;

    try {
      const { error } = await supabase
        .from('calls')
        .update({
          status: 'canceled',
          cancelled_at: new Date().toISOString(),
          cancelled_reason: reason || 'Cancelado pelo SDR - reunião não realizada',
        })
        .eq('id', meetingId);

      if (error) {
        console.error('Error canceling meeting:', error);
        return false;
      }

      // Remove from local state
      setPendingMeetings(prev => prev.filter(m => m.id !== meetingId));
      return true;
    } catch (err) {
      console.error('Error canceling meeting:', err);
      return false;
    }
  };

  useEffect(() => {
    loadPendingMeetings();

    // Refresh every 5 minutes
    const interval = setInterval(loadPendingMeetings, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [loadPendingMeetings]);

  return {
    pendingMeetings,
    loading,
    completeMeeting,
    cancelMeeting,
    refresh: loadPendingMeetings,
  };
}
